import React from 'react';
import IconButton from '../IconButton/IconButton';
import styles from './EncourageDonateModal.scss';
import Svg from '@justgiving/svg';
import DonateButton from '../DonateButton/DonateButton';
import socialShareImage from './assets/social-share.svg';
import usePageQuery, {
  buildQuery,
  UsePageQueryResult,
} from '../../hooks/usePageQuery';
import gql from 'graphql-tag';
import classNames from 'classnames';
import { closeIcon } from '@justgiving/icons';
import { useTranslation } from 'react-i18next';
import { Button } from '@justgiving/react-tracking';
import { EncourageDonateOwnerFragment } from '../../../types/__generated__';
import useAbEnrol from '../../hooks/useAbEnrol';
import { usePageOwner } from '../../hooks/usePageOwner';

interface EncourageDonateModalProps {
  onClose: () => void;
}

const FRAGMENT = buildQuery(gql`
  fragment EncourageDonateOwner on Page {
    owner {
      name
      avatar
    }
  }
`);

const EncourageDonateModal = React.forwardRef(
  ({ onClose }: EncourageDonateModalProps, ref?: React.Ref<HTMLDivElement>) => {
    const { isPageOwner } = usePageOwner();
    const { enrolledInVariation } = useAbEnrol(
      'fr_growth_share_prompt_profile_image',
    );
    const isEnrolledInSharePromptProfileImage = enrolledInVariation === 'B';
    const result = usePageQuery<EncourageDonateOwnerFragment>(FRAGMENT);
    const { t } = useTranslation('common', { useSuspense: false });

    if (isPageOwner || !UsePageQueryResult.hasData(result) || !result.data) {
      return null;
    }

    const { owner } = result.data;
    const avatarUrl = owner?.avatar;

    return (
      <div
        aria-label={t('page.encourageDonateModal')}
        className={styles.wrapper}
        data-qa="donate-modal"
        ref={ref}
      >
        <IconButton
          className={styles.close}
          onClick={onClose}
          title={t('page.closeEncourageDonateModal')}
        >
          <Svg markup={closeIcon} />
        </IconButton>
        <div className={styles.image}>
          <img
            alt={t('page.encourageDonateModalSocialShareImage')}
            className={classNames(
              styles.social,
              isEnrolledInSharePromptProfileImage && styles.avatar,
            )}
            src={
              (isEnrolledInSharePromptProfileImage && avatarUrl) ||
              socialShareImage
            }
          />
        </div>
        <div className={styles.description}>
          <h1 className={styles.heading}>
            {t('page.encourageDonateModalTitle')}
          </h1>
          <div className={styles.message}>
            {t('page.encourageDonateModalMessage')}
          </div>
          <DonateButton
            onClick={onClose}
            trackingDataPageSection="EncourageDonateModal"
          />
          <p>
            <Button
              className={styles.later}
              onClick={onClose}
              trackingData={{
                action_name: 'click',
                subtype: 'button',
                page_section: 'donate prompt',
                event: 'click',
                event_value: 'close',
              }}
            >
              {t('page.encourageDonateModalLaterDismiss')}
            </Button>
          </p>
        </div>
      </div>
    );
  },
);

EncourageDonateModal.displayName = 'EncourageDonateModal';

export default EncourageDonateModal;
